import React, { useState, useEffect } from "react";
import axios from "axios";

const SchoolAchievement = () => {
  const sessionId = localStorage.getItem("currentSessionId");

  const [date, setDate] = useState("");
  const [competitionTitle, setCompetitionTitle] = useState("");
  const [place, setPlace] = useState("");
  const [schoolRank, setSchoolRank] = useState("");
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [description, setDescription] = useState("");
  const [achievements, setAchievements] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (sessionId) fetchAchievements();
  }, [sessionId]);

  const fetchAchievements = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        ` ${process.env.REACT_APP_API_BASE_URL}/api/achievements/getSchoolAchievementsBySession/${sessionId}`
      );
      setAchievements(res.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching achievements!", error);
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setImage(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setImagePreview(null);
    }
  };

  const resetForm = () => {
    setDate("");
    setCompetitionTitle("");
    setPlace("");
    setSchoolRank("");
    setImage(null);
    setImagePreview(null);
    setDescription("");
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!sessionId) return alert("Session ID not found!");

    const formData = new FormData();
    formData.append("date", date);
    formData.append("competitionTitle", competitionTitle);
    formData.append("place", place);
    formData.append("schoolRank", schoolRank);
    formData.append("description", description);
    formData.append("sessionId", sessionId);
    if (image) formData.append("image", image);

    try {
      const url = editingId
        ? ` ${process.env.REACT_APP_API_BASE_URL}/api/achievements/updateAchievement/${editingId}`
        : ` ${process.env.REACT_APP_API_BASE_URL}/api/achievements/createAchievement`;
      const method = editingId ? "put" : "post";

      await axios[method](url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      alert(`Achievement ${editingId ? "Updated" : "Added"} Successfully`);
      resetForm();
      fetchAchievements();
    } catch (error) {
      console.error("Error submitting achievement!", error);
      alert(error.response?.data?.message || "Submission failed");
    }
  };

  const handleEdit = (a) => {
    setDate(a.date.split("T")[0]);
    setCompetitionTitle(a.competitionTitle);
    setPlace(a.place);
    setSchoolRank(a.schoolRank);
    setDescription(a.description);
    setEditingId(a._id);
    setImagePreview(a.image || null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this achievement?")) return;

    try {
      await axios.delete(` ${process.env.REACT_APP_API_BASE_URL}/api/achievements/deleteAchievement/${id}`);
      alert("Achievement Deleted Successfully");
      fetchAchievements();
    } catch (error) {
      console.error("Error deleting achievement!", error);
      alert(error.response?.data?.message || "Delete failed");
    }
  };

  const handleDownload = (id) => {
    window.open(` ${process.env.REACT_APP_API_BASE_URL}/api/achievements/download/${id}`, "_blank");
  };

  return (
    <div className="container mx-auto p-6 bg-white shadow-lg rounded-lg">
      <h1 className="text-xl font-bold mb-6 text-black">
        {editingId ? "Edit School Achievement" : "Add School Achievement"}
      </h1>
      <hr />
      <br />

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex flex-col">
            <label>Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="border rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div className="flex flex-col">
            <label>Competition Title</label>
            <input
              type="text"
              value={competitionTitle}
              onChange={(e) => setCompetitionTitle(e.target.value)}
              className="border rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div className="flex flex-col">
            <label>Place</label>
            <input
              type="text"
              value={place}
              onChange={(e) => setPlace(e.target.value)}
              className="border rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div className="flex flex-col">
            <label>School Rank</label>
            <input
              type="text"
              value={schoolRank}
              onChange={(e) => setSchoolRank(e.target.value)}
              className="border rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div className="flex flex-col md:col-span-2">
            <label>Upload Image (Optional)</label>
            <input
              type="file"
              onChange={handleImageChange}
              className="border rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500"
            />
            {imagePreview && (
              <img
                src={imagePreview}
                alt="Preview"
                className="mt-2 w-32 h-32 object-cover rounded"
              />
            )}
          </div>

          <div className="flex flex-col md:col-span-2">
            <label>Description</label>
            <textarea
              rows="4"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="border rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              required
            ></textarea>
          </div>
        </div>

        <div className="flex justify-start gap-10">
          <button
            type="submit"
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg"
          >
            {editingId ? "Update Achievement" : "Add Achievement"}
          </button>

          <button
            type="button"
            onClick={resetForm}
            className="bg-red-600 hover:bg-red-400 text-white px-4 py-2 rounded-lg"
          >
            Reset
          </button>
        </div>
      </form>

      <h2 className="text-xl font-bold mt-12 mb-6 text-black">Achievements List</h2>
      <hr />
      <br />

      {loading ? (
        <p className="text-center text-gray-600">Loading...</p>
      ) : achievements.length === 0 ? (
        <p className="text-center text-gray-600">No achievements found.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {achievements.map((a) => (
            <div key={a._id} className="p-5 bg-gray-50 border rounded-lg shadow">
              <h3 className="text-xl font-semibold mb-2">{a.competitionTitle}</h3>
              <p><strong>Date:</strong> {new Date(a.date).toLocaleDateString()}</p>
              <p><strong>Place:</strong> {a.place}</p>
              <p><strong>Rank:</strong> {a.schoolRank}</p>
              <p>{a.description}</p>
              {a.image && (
                <div className="flex items-center space-x-3 mt-2">
                  <a
                    href={a.image}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline text-sm"
                  >
                    View
                  </a>
                  <button
                    onClick={() => handleDownload(a._id)}
                    className="bg-green-500 hover:bg-green-600 text-white px-2 py-1 rounded text-sm"
                  >
                    Download
                  </button>
                </div>
              )}
              <div className="flex space-x-3 mt-3">
                <button
                  onClick={() => handleEdit(a)}
                  className="bg-yellow-500 text-white px-3 py-2 rounded hover:bg-yellow-400"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(a._id)}
                  className="bg-red-600 text-white px-3 py-2 rounded hover:bg-red-500"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SchoolAchievement;